// src/pages/talentScout/getOrganization.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProfileImage from "../../components/profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const GetOrganization = () => {
  const { t } = useTranslation();
  const [organization, setOrganization] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");
  const { id: organizationId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        // Fetch organization and teams data in parallel
        const [orgResponse, teamsResponse] = await Promise.all([
          fetch(
            `${API_URL}/talent-scout/getOrganizationById?organizationId=${organizationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          ),
          fetch(
            `${API_URL}/talent-scout/getOrganizationTeams?organizationId=${organizationId}&page=${page}&search=${searchTerm}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          ),
        ]);

        if (!orgResponse.ok || !teamsResponse.ok) {
          throw new Error("Failed to fetch data");
        }

        const [orgData, teamsData] = await Promise.all([
          orgResponse.json(),
          teamsResponse.json(),
        ]);

        setOrganization(orgData);
        setTeams(teamsData.teams);
        setTotalPages(teamsData.totalPages);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchData();
    }
  }, [organizationId, page, searchTerm]);

  const getOrganizationAge = () => {
    if (!organization?.createdAt) return "N/A";
    const created = new Date(organization.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years`;
  };

  const calculateTeamStats = () => {
    if (!teams || teams.length === 0) return { totalPlayers: 0, totalMembers: 0, sports: {}, types: {} };
    
    return {
      totalPlayers: teams.reduce((sum, team) => sum + (team.members?.filter(m => m.type === "player").length || 0), 0),
      totalMembers: teams.reduce((sum, team) => sum + (team.members?.length || 0), 0),
      sports: teams.reduce((acc, team) => {
        acc[team.sport] = (acc[team.sport] || 0) + 1;
        return acc;
      }, {}),
      types: teams.reduce((acc, team) => {
        acc[team.type] = (acc[team.type] || 0) + 1;
        return acc;
      }, {})
    };
  };

  const stats = calculateTeamStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">{t("talentScout.loadingOrganizationDetails")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-500 text-xl font-semibold mb-2">⚠️ {t("talentScout.error")}</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            ← {t("talentScout.goBack")}
          </button>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-xl font-semibold mb-2">🏢 Organization Not Found</div>
          <div className="text-gray-600 mb-4">{t("talentScout.requestedOrganizationNotFound")}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            ← {t("talentScout.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t("talentScout.backToSearch")}
        </button>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-xl overflow-hidden mb-8" style={{background: "linear-gradient(135deg, #EC6B37 0%, #d55a2e 100%)"}}>
          <div className="p-8 text-white">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
              <div className="flex-shrink-0">
                <ProfileImage
                  image={organization.profilePicture}
                  alt={organization.userName}
                  className="w-32 h-32 rounded-2xl object-cover ring-4 ring-white/20"
                />
              </div>
              <div className="flex-grow">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  <div>
                    <h1 className="text-4xl font-bold mb-2">
                      {organization.firstName} {organization.lastName}
                    </h1>
                    <p className="text-orange-100 text-lg mb-4">@{organization.userName}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        🏢 {t("talentScout.organization")}
                      </span>
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        📅 {getOrganizationAge()} {t("talentScout.yearsOld")}
                      </span>
                      <span className="bg-green-500/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        ✓ {t("talentScout.active")}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Link
                      to={`/feed/${organization._id}`}
                      className="px-6 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                                              {t("talentScout.viewFeed")}
                      </Link>
                      <Link
                        to={`/chat/${organization._id}`}
                        className="px-6 py-3 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                        </svg>
                        {t("talentScout.startChat")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Teams</p>
                <p className="text-3xl font-bold text-gray-900">{teams.length}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Players</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalPlayers}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Members</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalMembers}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Avg Players/Team</p>
                <p className="text-3xl font-bold text-gray-900">
                  {teams.length > 0 ? Math.round(stats.totalPlayers / teams.length) : 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {["overview", "teams", "analytics"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t(`talentScout.${tab}`)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-8">
                {/* Organization Details */}
                <div>
                                      <h3 className="text-xl font-semibold mb-4">{t("talentScout.organizationInformation")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.username")}</p>
                      <p className="text-lg font-semibold text-gray-900">@{organization.userName}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.email")}</p>
                      <p className="text-lg font-semibold text-gray-900">{organization.email}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.phone")}</p>
                      <p className="text-lg font-semibold text-gray-900">{organization.phone || t("talentScout.notProvided")}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.gender")}</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">{organization.gender || t("talentScout.notProvided")}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.accountType")}</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">{organization.accountType}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 font-medium">{t("talentScout.memberSince")}</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {new Date(organization.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sports & Types Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Sports Distribution */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4">{t("talentScout.sportsDistribution")}</h3>
                    <div className="space-y-3">
                      {Object.entries(stats.sports).map(([sport, count]) => (
                                                 <div key={sport} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                           <span className="font-medium text-gray-900">{sport}</span>
                           <div className="flex items-center gap-2">
                                                           <div className="bg-orange-200 rounded-full px-3 py-1 text-sm font-medium text-orange-800">
                                {count} {count === 1 ? t("talentScout.team") : t("talentScout.teams")}
                              </div>
                            <div className="text-sm text-gray-500">
                              {Math.round((count / teams.length) * 100)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Team Types Distribution */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4">{t("talentScout.teamTypesDistribution")}</h3>
                    <div className="space-y-3">
                      {Object.entries(stats.types).map(([type, count]) => (
                                                 <div key={type} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                           <span className="font-medium text-gray-900">{type}</span>
                           <div className="flex items-center gap-2">
                                                           <div className="bg-emerald-200 rounded-full px-3 py-1 text-sm font-medium text-emerald-800">
                                {count} {count === 1 ? t("talentScout.team") : t("talentScout.teams")}
                              </div>
                            <div className="text-sm text-gray-500">
                              {Math.round((count / teams.length) * 100)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === "teams" && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h3 className="text-xl font-semibold">{t("talentScout.teams")} ({teams.length})</h3>
                  <div className="flex gap-4">
                    <input
                      type="text"
                      placeholder={t("talentScout.searchTeams")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {teams.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">{t("talentScout.noTeamsFound")}</p>
                    <p className="text-gray-400 text-sm">{t("talentScout.noTeamsAdded")}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {teams.map((team) => (
                      <Link
                        key={team._id}
                        to={`/talent-scout/get-team/${team._id}`}
                        className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-orange-300 transition-all duration-200"
                      >
                        <div className="p-6">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <img
                                src={team.logo}
                                alt={team.name}
                                className="w-16 h-16 rounded-lg object-cover ring-2 ring-gray-100 group-hover:ring-orange-200 transition-all"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div 
                                className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg"
                                style={{ display: 'none' }}
                              >
                                {team.name?.charAt(0)?.toUpperCase() || 'T'}
                              </div>
                            </div>
                            <div className="flex-grow min-w-0">
                                                             <h4 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors truncate">
                                {team.name}
                              </h4>
                              <p className="text-sm text-gray-500 truncate">@{team.uniqueName}</p>
                              <div className="mt-3 flex flex-wrap gap-2">
                                                                 <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                                  {team.sport}
                                </span>
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium capitalize">
                                  {team.gender}
                                </span>
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                                  {team.type}
                                </span>
                              </div>
                              <div className="mt-3 flex items-center text-sm text-gray-500">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                </svg>
                                {team.members?.filter(m => m.type === "player").length || 0} players
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      {t("common.previous")}
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                                                         className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                               page === pageNum
                                 ? "bg-orange-500 text-white"
                                 : "text-gray-500 hover:bg-gray-100"
                             }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      {t("talentScout.next")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === "analytics" && (
              <div className="space-y-8">
                <h3 className="text-xl font-semibold">{t("talentScout.organizationAnalytics")}</h3>
                
                {/* Performance Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                     <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl">
                     <h4 className="font-semibold text-orange-900 mb-2">{t("talentScout.teamPerformance")}</h4>
                     <div className="space-y-2">
                       <div className="flex justify-between">
                         <span className="text-orange-700">{t("talentScout.mostActiveSport")}:</span>
                         <span className="font-medium text-orange-900">
                          {Object.entries(stats.sports).sort(([,a], [,b]) => b - a)[0]?.[0] || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                                                 <span className="text-orange-700">{t("talentScout.largestTeamType")}:</span>
                         <span className="font-medium text-orange-900">
                          {Object.entries(stats.types).sort(([,a], [,b]) => b - a)[0]?.[0] || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                                     <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl">
                     <h4 className="font-semibold text-emerald-900 mb-2">{t("talentScout.teamEfficiency")}</h4>
                     <div className="space-y-2">
                       <div className="flex justify-between">
                         <span className="text-emerald-700">{t("talentScout.avgTeamSize")}:</span>
                         <span className="font-medium text-emerald-900">
                          {teams.length > 0 ? Math.round(stats.totalMembers / teams.length) : 0} members
                        </span>
                      </div>
                      <div className="flex justify-between">
                                                 <span className="text-emerald-700">{t("talentScout.playerRatio")}:</span>
                         <span className="font-medium text-emerald-900">
                          {Math.round((stats.totalPlayers / Math.max(stats.totalMembers, 1)) * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                    <h4 className="font-semibold text-purple-900 mb-2">{t("talentScout.organizationHealth")}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-purple-700">{t("talentScout.teamsStatus")}:</span>
                        <span className="font-medium text-purple-900">
                          {teams.length > 0 ? t("talentScout.active") : t("talentScout.inactive")}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">Organization Age:</span>
                        <span className="font-medium text-purple-900">{getOrganizationAge()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Rankings */}
                {teams.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold mb-4">{t("talentScout.teamRankingsBySize")}</h4>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="divide-y divide-gray-200">
                        {teams
                          .sort((a, b) => (b.members?.length || 0) - (a.members?.length || 0))
                          .slice(0, 5)
                          .map((team, index) => (
                            <div key={team._id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                              <div className="flex items-center space-x-4">
                                                                 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                   index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-orange-500'
                                 }`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-900">{team.name}</h5>
                                  <p className="text-sm text-gray-500">{team.sport} • {team.type}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-gray-900">{team.members?.length || 0} members</p>
                                <p className="text-sm text-gray-500">
                                  {team.members?.filter(m => m.type === "player").length || 0} players
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetOrganization;
